import React, { useState } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { I18n } from '@aws-amplify/core';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

import { ActionPlansIcon } from '@/assets/icons/v2';
import { ActionPlanModal } from '@/components/ActionPlan';
import { ModalDelete } from '@/components/ui/Modals/ModalDelete';
import { useDeleteActionPlan } from '@/hooks/v2/useDeleteActionPlan';
import { useBeraJobSummaryResult } from '@/context/BeraJobSummaryResult';
import { RiskBadge } from '@/components/views/BeraJob/components/RiskBadge';
import { useGetBeraJobSummaryActionPlans } from '@/hooks/useGetBeraJobSummaryActionPlans';
import { ActionPlanCard } from '@/components/views/BeraJob/Results/ActionPlans/ActionPlanCard';

import { Accordion } from '../Accordion';
import { PaginationArrow } from '../AnalyzedActivities/styles';
import { Body, CardsPagination, CreateButton, EmptyState, Total } from './styles';

const BLOCK_GUTTER = 16;
const FIRST_PAGE = 1;
const PAGE_SIZE = 1;
const TABLE_ORIGIN = 'bera_job_summaries';
const ORIGIN_NAME = 'BERA';

const MENU_KEY = {
	EDIT: 'edit',
	DELETE: 'delete'
} as const;

interface ActionPlansAccordionProps {
	jobSummaryId: string;
	fileId: string;
	workstationId: string;
	rpn: number;
	open: boolean;
	onToggle: () => void;
}

function renderPaginationItem(
	_page: number,
	type: 'page' | 'prev' | 'next' | 'jump-prev' | 'jump-next',
	element: React.ReactNode
): React.ReactNode {
	if (type === 'prev') {
		return <PaginationArrow $direction="previous" />;
	}

	if (type === 'next') {
		return <PaginationArrow $direction="next" />;
	}

	return element;
}

export function ActionPlansAccordion({
	jobSummaryId,
	fileId,
	workstationId,
	rpn,
	open,
	onToggle
}: Readonly<ActionPlansAccordionProps>): JSX.Element {
	const { organizationId, companyId } = useBeraJobSummaryResult();

	const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [actionPlanId, setActionPlanId] = useState<string | null>(null);
	const [planToDeleteId, setPlanToDeleteId] = useState<string | null>(null);

	const { mutate: deleteActionPlan, isLoading: isDeleting } = useDeleteActionPlan();

	const actionPlans = useGetBeraJobSummaryActionPlans({
		organization_id: organizationId,
		company_id: companyId,
		id: jobSummaryId
	});

	const plans = actionPlans.data ?? [];
	const total = plans.length;
	const currentPlan = plans[currentPage - FIRST_PAGE];
	const hasPlans = total > 0;
	const isLoading = actionPlans.isLoading || actionPlans.isFetching;
	const totalText = `${I18n.get('Displaying')} ${currentPage}-${currentPage} ${I18n.get('of')} ${total}`;

	function handleCreate(event: React.MouseEvent<HTMLElement>): void {
		event.stopPropagation();
		setActionPlanId(null);
		setIsModalOpen(true);
	}

	function handleEdit(): void {
		if (!currentPlan) {
			return;
		}

		setActionPlanId(currentPlan.id);
		setIsModalOpen(true);
	}

	function handleOpenDelete(): void {
		if (!currentPlan) {
			return;
		}

		setPlanToDeleteId(currentPlan.id);
	}

	function handleCloseDelete(): void {
		setPlanToDeleteId(null);
	}

	function handleDeleted(): void {
		const remaining = total - 1;
		const remainingPages = Math.ceil(remaining / PAGE_SIZE);
		const lastPage = Math.max(remainingPages, FIRST_PAGE);

		setPlanToDeleteId(null);

		if (currentPage > lastPage) {
			setCurrentPage(lastPage);
		}

		actionPlans.refetch();
	}

	function handleConfirmDelete(): void {
		if (!planToDeleteId) {
			return;
		}

		const params = { ids: [planToDeleteId], organization_id: organizationId, company_id: companyId };

		deleteActionPlan(params, { onSuccess: handleDeleted });
	}

	function handleCloseModal(): void {
		setIsModalOpen(false);
		setActionPlanId(null);
		actionPlans.refetch();
	}

	function handlePageChange(page: number): void {
		setCurrentPage(page);
	}

	const menuItems: MenuProps['items'] = [
		{
			key: MENU_KEY.EDIT,
			icon: <EditOutlined />,
			label: I18n.get('Edit'),
			onClick: handleEdit
		},
		{
			key: MENU_KEY.DELETE,
			icon: <DeleteOutlined />,
			label: I18n.get('Delete'),
			danger: true,
			onClick: handleOpenDelete
		}
	];

	const createButton = (
		<CreateButton icon={<ActionPlansIcon />} onClick={handleCreate}>
			{I18n.get('Create action plan')}
		</CreateButton>
	);

	return (
		<>
			<Accordion title={I18n.get('Action plans')} open={open} onToggle={onToggle} extra={createButton}>
				<Body>
					{isLoading && <Skeleton active />}
					{!isLoading && !hasPlans && <EmptyState>{I18n.get('No action plans')}</EmptyState>}
					{!isLoading && hasPlans && !!currentPlan && (
						<Row gutter={[0, BLOCK_GUTTER]}>
							<Col span={24}>
								<ActionPlanCard data={currentPlan} menuItems={menuItems} />
							</Col>
							<Col span={24}>
								<Row align="middle" justify="space-between">
									<Col>
										<Total>{totalText}</Total>
									</Col>
									<Col>
										<CardsPagination
											simple={false}
											current={currentPage}
											total={total}
											pageSize={PAGE_SIZE}
											showSizeChanger={false}
											itemRender={renderPaginationItem}
											onChange={handlePageChange}
										/>
									</Col>
								</Row>
							</Col>
						</Row>
					)}
				</Body>
			</Accordion>
			{isModalOpen && (
				<ActionPlanModal
					open
					onClose={handleCloseModal}
					table_origin={TABLE_ORIGIN}
					table_origin_id={jobSummaryId}
					file_id={fileId}
					workstation_id={workstationId}
					origin_name={ORIGIN_NAME}
					setActionPlanId={setActionPlanId}
					risk={<RiskBadge taskRPN={rpn} />}
					action_plan_id={actionPlanId}
					onCreateOrUpdate={handleCloseModal}
				/>
			)}
			<ModalDelete
				open={!!planToDeleteId}
				loading={isDeleting}
				description={I18n.get('Heads up! Want to delete this action plan?')}
				onCancel={handleCloseDelete}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
}
